import apis from '#store/api';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataApiResponse, DataCollectionApiResponse } from "./types";

const ACCOUNT_REQUESTS_KEY = 'account-requests';

export interface AccountRequest {
    id?: string;
    firstName?: string;
    lastName?: string;
    organizationName?: string;
    email?: string;
    phoneNumber?: string;
}

interface CreateAccountRequestParams {
    accountRequest: Omit<AccountRequest, 'id'>;
    isNewOrganization: boolean;
    recaptchaToken: string;
}

interface GetAccountRequestsParams {
    skip?: number;
    take: number;
}

interface BatchProcessAccountRequestsParams {
    ids: string[];
    isApproved: boolean;
    role?: string;
}

// Fetch account requests list
export const useAccountRequests = (params: GetAccountRequestsParams) => {
    return useQuery({
        queryKey: [ACCOUNT_REQUESTS_KEY, params],
        queryFn: async () => {
            const response = await apis.accountRequests.getAccountRequestsList({ params });
            return response.data as DataCollectionApiResponse<AccountRequest>;
        },
    });
};

// Create account request
export const useCreateAccountRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (newAccountRequest: CreateAccountRequestParams) => {
            const response = await apis.accountRequests.createAccountRequest({ data: newAccountRequest });
            return response.data as DataApiResponse<AccountRequest>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_REQUESTS_KEY] });
        },
    });
};

// Batch process account requests
export const useBatchProcessAccountRequests = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchProcessAccountRequestsParams) => {
            const response = await apis.accountRequests.batchProcessAccountRequests({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_REQUESTS_KEY] });
        },
    });
};